import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {ProductRecommendationDgSearchResults} from "../models/datagrid-search-results/product-recommendation.dg-search-results.model";
import {ProductRecommendation} from "../models/product-recommendation.model";
import {User} from "../models/user.model";

/**
 * Serwis warstwy danych zarządzający modułem rekomendacji produktowych.
 * Umożliwia wiązanie asortymentu partnerskiego z konkretnymi pacjentami/użytkownikami,
 * obsługę procesów tworzenia poleceń oraz zasilanie widoków tabelarycznych (DataGrid).
 */
@Injectable({
  providedIn: 'root'
})
export class ProductRecommendationService implements DataGridServiceInterface<Observable<ProductRecommendationDgSearchResults>> {

  constructor(private apiService: ApiService, public commonUtils: CommonUtils) {
  }

  /**
   * Pobiera paginowaną listę wystawionych rekomendacji produktowych.
   *
   * @param limit Maksymalna liczba rekordów na stronę.
   * @param offset Przesunięcie wyników względem początku zbioru.
   * @param sortOrder Kierunek sortowania (np. 'ASC', 'DESC').
   * @param orderBy Nazwa kolumny, według której aplikowane jest sortowanie.
   * @param showDeletedObjects Flaga decydująca o widoczności usuniętych/anulowanych rekomendacji.
   * @returns Observable emitujący ustandaryzowany obiekt z wynikami dla tabeli DataGrid.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<ProductRecommendationDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/product-recommendations/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        const productRecommendations = new ProductRecommendationDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new ProductRecommendation(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
        return productRecommendations;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Filtruje i wyszukuje rekomendacje na podstawie zaawansowanych parametrów
   * (np. ID lekarza polecającego, ID pacjenta, status konwersji/zakupu).
   *
   * @param limit Rozmiar strony.
   * @param offset Przesunięcie.
   * @param searchFieldsData Mapa zawierająca zdefiniowane filtry wyszukiwania.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Nazwa kolumny sortującej.
   * @param showDeletedObjects Flaga widoczności usuniętych rekordów.
   * @returns Observable emitujący przefiltrowaną listę rekomendacji dla DataGrid.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<ProductRecommendationDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>('/dashboard/product-recommendations/search', data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response.results);
        return new ProductRecommendationDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new ProductRecommendation(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pełne szczegóły pojedynczej rekomendacji.
   *
   * @param uuid Unikalny identyfikator rekomendacji.
   * @returns Observable emitujący obiekt szczegółów rekomendacji.
   */
  getDetails(uuid: string): Observable<ProductRecommendation> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/product-recommendations/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<ProductRecommendation>(new ProductRecommendation(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje w systemie nowo wygenerowaną rekomendację produktu dla pacjenta.
   *
   * @param productRecomendation Obiekt zawierający dane tworzonej rekomendacji.
   * @returns Observable emitujący zaktualizowany obiekt zwrócony z backendu.
   */
  add(productRecomendation: ProductRecommendation): Observable<ProductRecommendation> {
    return this.apiService.post<VetineoApiResponse>('/dashboard/product-recommendations', productRecomendation).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<ProductRecommendation>(new ProductRecommendation(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukiwarka tekstowa (Typeahead) użytkowników/pacjentów, do których można
   * przypisać rekomendację. Ogranicza wyniki wyszukiwania do kontekstu danego klienta.
   *
   * @param dataToFind Szukana fraza.
   * @param vClientUUID Identyfikator nadrzędnego klienta, ograniczający zakres wyszukiwania pacjentów.
   * @returns Observable emitujący tablicę dopasowanych obiektów User.
   */
  searchForUserByPhraseToRecommendProduct(dataToFind: string, vClientUUID: string): Observable<User[]> {
    const body = {
      phrase: dataToFind,
      relatedItemUUID: vClientUUID
    };
    return this.apiService.post<VetineoApiResponse>('/dashboard/users-to-recommend/by-phrase', body, {}).pipe(take(1), map(vetineoApiResponse => {
      if (!vetineoApiResponse.errors && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedArrayCopyEver(new User(), [], vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors?.main);
      }
    }));
  }
}
